/**
 * @fileoverview ESM mirror of ../registry.js.
 *
 * Vite + native Node ESM consumers resolve through this entry via the
 * package.json `import` / `browser` conditions. CJS consumers hit the
 * parallel `../registry.js` via the `require` condition.
 *
 * Drift between this file and the CJS source is caught by
 * `tests/esm/native-import.test.mjs`.
 *
 * @module @puffin-core/cross-app-policy/registry
 */

const SOURCE_MODES = Object.freeze({
  TRUSTED: 'trusted',
  SHARED_TERMINAL: 'shared-terminal',
  PLATFORM_ADMIN: 'platform-admin',
});

const TARGET_MODES = Object.freeze({
  SSO_RECEIVABLE: 'sso-receivable',
  SSO_NOT_RECEIVABLE: 'sso-not-receivable',
});

function makePolicy({
  appKey,
  sourceMode,
  targetMode,
  domainEnvVar,
  defaultDomain,
  webDevPort,
  apiDevPort,
}) {
  return Object.freeze({
    appKey,
    sourceMode,
    targetMode,
    domainEnvVar,
    defaultDomain,
    webDevPort,
    apiDevPort,
  });
}

const APP_REGISTRY = Object.freeze({
  palm: makePolicy({
    appKey: 'palm',
    sourceMode: SOURCE_MODES.TRUSTED,
    targetMode: TARGET_MODES.SSO_RECEIVABLE,
    domainEnvVar: 'PALM_DOMAIN',
    defaultDomain: 'palmpuffin.com',
    webDevPort: 5800,
    apiDevPort: 3600,
  }),
  parcel: makePolicy({
    appKey: 'parcel',
    sourceMode: SOURCE_MODES.SHARED_TERMINAL,
    targetMode: TARGET_MODES.SSO_RECEIVABLE,
    domainEnvVar: 'PARCEL_DOMAIN',
    defaultDomain: 'parcelpuffin.com',
    webDevPort: 5173,
    apiDevPort: 3001,
  }),
  patrol: makePolicy({
    appKey: 'patrol',
    sourceMode: SOURCE_MODES.TRUSTED,
    targetMode: TARGET_MODES.SSO_RECEIVABLE,
    domainEnvVar: 'PATROL_DOMAIN',
    defaultDomain: 'patrolpuffin.com',
    webDevPort: 5600,
    apiDevPort: 3200,
  }),
  payday: makePolicy({
    appKey: 'payday',
    sourceMode: SOURCE_MODES.TRUSTED,
    targetMode: TARGET_MODES.SSO_RECEIVABLE,
    domainEnvVar: 'PAYDAY_DOMAIN',
    defaultDomain: 'paydaypuffin.com',
    webDevPort: 5500,
    apiDevPort: 3300,
  }),
  planner: makePolicy({
    appKey: 'planner',
    sourceMode: SOURCE_MODES.TRUSTED,
    targetMode: TARGET_MODES.SSO_RECEIVABLE,
    domainEnvVar: 'PLANNER_DOMAIN',
    defaultDomain: 'plannerpuffin.com',
    webDevPort: 5700,
    apiDevPort: 3500,
  }),
  port: makePolicy({
    appKey: 'port',
    sourceMode: SOURCE_MODES.TRUSTED,
    targetMode: TARGET_MODES.SSO_RECEIVABLE,
    domainEnvVar: 'PORT_DOMAIN',
    defaultDomain: 'portpuffin.com',
    webDevPort: 5400,
    apiDevPort: 3800,
  }),
  prep: makePolicy({
    appKey: 'prep',
    sourceMode: SOURCE_MODES.TRUSTED,
    targetMode: TARGET_MODES.SSO_RECEIVABLE,
    domainEnvVar: 'PREP_DOMAIN',
    defaultDomain: 'preppuffin.com',
    webDevPort: 5900,
    apiDevPort: 3700,
  }),
  // PROSPECT-001 (2026-06-13) — ProspectPuffin business-development app.
  // Mirrors ../registry.js (CJS). SSO-receivable launch target for the
  // existing Port/PayDay launchers; trusted source for future mint.
  prospect: makePolicy({
    appKey: 'prospect',
    sourceMode: SOURCE_MODES.TRUSTED,
    targetMode: TARGET_MODES.SSO_RECEIVABLE,
    domainEnvVar: 'PROSPECT_DOMAIN',
    defaultDomain: 'prospectpuffin.com',
    webDevPort: 5903,
    apiDevPort: 4100,
  }),
  // MKT-LOGIN-XDOMAIN-001 (2026-06-29) — PurchasePuffin (ecomm) as a TRUSTED
  // single-user cross-app citizen. Mirrors ../registry.js (CJS). `appKey:
  // 'purchase'` matches @puffin-core/brand-assets BRAND_REGISTRY.
  purchase: makePolicy({
    appKey: 'purchase',
    sourceMode: SOURCE_MODES.TRUSTED,
    targetMode: TARGET_MODES.SSO_RECEIVABLE,
    domainEnvVar: 'PURCHASE_DOMAIN',
    defaultDomain: 'purchasepuffin.com',
    webDevPort: 5200,
    apiDevPort: 3100,
  }),
});

const APP_KEYS = Object.freeze(Object.keys(APP_REGISTRY));

// The PLATFORM console, OUTSIDE APP_REGISTRY / APP_KEYS (see the CJS source).
const PLATFORM_CONSOLE = Object.freeze({
  appKey: 'console',
  domainEnvVar: 'CONSOLE_DOMAIN',
  defaultDomain: 'console.parcelpuffin.com',
  webDevPort: 5300,
  securityPath: '/security',
});

export { APP_REGISTRY, APP_KEYS, PLATFORM_CONSOLE, SOURCE_MODES, TARGET_MODES };
